import { useAgents } from '../../hooks/useAgents';
import Badge, { getStatusVariant } from '../../components/ui/Badge';
import { RotateCcw } from 'lucide-react';
import type { Agent } from '../../types';

interface AgentSelectorProps {
  selectedAgentId: string | null;
  onSelect: (agentId: string) => void;
  onReset: () => void;
}

export default function AgentSelector({
  selectedAgentId,
  onSelect,
  onReset,
}: AgentSelectorProps) {
  const { data, isLoading, error } = useAgents();
  const agents = data?.items ?? [];

  return (
    <div className="flex h-full w-72 shrink-0 flex-col border-r border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">Agents</h2>
        {selectedAgentId && (
          <button
            type="button"
            onClick={onReset}
            className="focus-ring flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            New Chat
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {isLoading && (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            Loading agents...
          </div>
        )}

        {error && (
          <div className="px-3 py-8 text-center text-sm text-red-500">
            Failed to load agents
          </div>
        )}

        {!isLoading && agents.length === 0 && (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            No agents found
          </div>
        )}

        {agents.map((agent: Agent) => (
          <button
            key={agent.agentId}
            type="button"
            onClick={() => onSelect(agent.agentId)}
            className={`w-full rounded-md px-3 py-2.5 text-left transition-colors ${
              selectedAgentId === agent.agentId
                ? 'bg-primary-50 dark:bg-primary-900/30 ring-1 ring-primary-200'
                : 'hover:bg-muted'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground truncate">
                {agent.name}
              </span>
              <Badge variant={getStatusVariant(agent.status)}>
                {agent.status}
              </Badge>
            </div>
            {agent.description && (
              <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                {agent.description}
              </p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
